<template>
  <button 
    class="service-card" 
    :class="[`service-card--${variant}`, { 'service-card--clickable': isClickable }]"
    @click="handleClick"
  >
    <div class="service-card__header">
      <h3 class="service-card__title text-left">{{ title }}</h3>
      <svg v-if="showArrow" class="service-card__arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <p class="service-card__description text-left">{{ description }}</p>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  variant: {
    type: String,
    default: 'blue',
    validator: (value) => ['blue', 'teal', 'light-blue'].includes(value)
  },
  routeTo: {
    type: String,
    default: ''
  },
  showArrow: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['click'])

const isClickable = computed(() => props.routeTo || props.showArrow)

const handleClick = () => {
  if (props.routeTo || isClickable.value) {
    emit('click', props.routeTo)
  }
}
</script>

<style scoped>
.service-card {
  border-radius: 12px;
  padding: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 140px;
}

.service-card--clickable {
  cursor: pointer;
}

.service-card--clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.service-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.service-card__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: white;
}

.service-card__arrow {
  flex-shrink: 0;
  color: white;
  opacity: 0.9;
}

.service-card__description {
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
}

/* Variants */
.service-card--blue {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.service-card--teal {
  background: linear-gradient(135deg, #4db8a8 0%, #3a9688 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.service-card--light-blue {
  background: linear-gradient(135deg, #5eb3e4 0%, #4a9fd1 100%);
  border: 2px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .service-card {
    min-height: 120px;
  }

  .service-card__title {
    font-size: 1.1rem;
  }

  .service-card__description {
    font-size: 0.85rem;
  }
}
</style>
